import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Check if an interview is stale (older than 5 days since last status update)
 * @param {string} lastUpdated - ISO date string of last update (from 'Last Updated' field)
 * @param {string} lastModified - ISO date string of last modification (fallback)
 * @returns {boolean} - true if stale
 */
export const isInterviewStale = (lastUpdated, lastModified) => {
  // Use our custom 'Last Updated' field first, fallback to Airtable's timestamp
  const timestamp = lastUpdated || lastModified;
  if (!timestamp) return false;
  
  const fiveDaysAgo = new Date();
  fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
  
  const timestampDate = new Date(timestamp);
  return timestampDate < fiveDaysAgo;
};

/**
 * Check for stale interviewing roles that need status updates
 * @param {Array} interviews - Array of interview objects
 * @returns {Array} - Array of stale interviewing roles
 */
export const getStaleInterviewingRoles = (interviews) => {
  if (!Array.isArray(interviews)) return [];
  
  return interviews.filter(interview => {
    // Check if the stage is "Interviewing"
    const stage = interview.stage || interview.fields?.Stage;
    if (stage !== "Interviewing") return false;
    
    // Check if the candidate interview stage is still "Interviewing" (not updated to specific status)
    const candidateStage = interview['Candidate Interview Stage'] || interview.fields?.['Candidate Interview Stage'];
    
    // If candidate stage is different from "Interviewing" or null, it means they've already updated
    if (candidateStage && candidateStage !== "Interviewing") return false;
    
    // Check the last updated date - try different field names that might exist
    const lastUpdated = 
      interview['Last Updated'] ||
      interview.fields?.['Last Updated'] ||
      interview.lastUpdated ||
      interview.fields?.lastUpdated;
    
    const lastModified = 
      interview['Candidate Stage (last modified)'] ||
      interview.fields?.['Candidate Stage (last modified)'] ||
      interview['Candidate Interview Stage (last modified)'] ||
      interview.fields?.['Candidate Interview Stage (last modified)'] ||
      interview.lastModified ||
      interview.fields?.lastModified;
    
    return isInterviewStale(lastUpdated, lastModified);
  });
};

/**
 * Check if the stale interview popup should be shown
 * Returns true if there are stale interviews - no dismissal logic, always shows until resolved
 * @param {Array} interviews - Array of interview objects
 * @returns {Object} - { shouldShow: boolean, staleInterviews: Array }
 */
export const shouldShowStaleInterviewPopup = (interviews) => {
  const staleInterviews = getStaleInterviewingRoles(interviews);
  
  if (staleInterviews.length === 0) {
    return { shouldShow: false, staleInterviews: [] };
  }
  
  // Clear any existing dismissal data since we no longer use dismissal logic
  localStorage.removeItem('staleInterviewPopupDismissed');
  
  // Always show the popup if there are stale interviews
  return { shouldShow: true, staleInterviews };
};

/**
 * Mark the stale interview popup as dismissed
 * Note: This function is now a no-op since we want the popup to always show until resolved
 */
export const dismissStaleInterviewPopup = () => {
  // No-op: We no longer use dismissal logic
  // The popup will always show until there are no stale interviews
};

// Authentication redirect utilities
export const setRedirectUrl = (url) => {
  // Only store if it's not an auth page
  if (url && !url.includes('/login') && !url.includes('/signup') && !url.includes('/forgot-password') && !url.includes('/reset-password')) {
    localStorage.setItem('redirectUrl', url);
  }
};

export const getRedirectUrl = () => {
  return localStorage.getItem('redirectUrl');
};

export const clearRedirectUrl = () => {
  localStorage.removeItem('redirectUrl');
};

export const getDefaultRedirectUrl = () => {
  return '/roles'; // Default fallback
};
